import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Home, Info, Brain, Wrench, Star, Tag, HelpCircle, Mail, Calendar, Scale, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import Logo from './Logo';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { id: 1, tile: "WHY AI", path: "/#why-ai" },
    { id: 2, tile: "SERVICES", path: "/#services" },
    { id: 3, tile: "SUCCESS STORIES", path: "/#success-stories" },
    { id: 4, tile: "OFFERS", path: "/#offers" },
    { id: 5, tile: "BOOK NOW", path: "/book" }
  ];

  const handleNavigation = async (path: string) => {
    if (path.startsWith('/#')) {
      if (location.pathname !== '/') {
        await navigate('/');
        setTimeout(() => {
          const element = document.querySelector(path.replace('/', ''));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(path.replace('/', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      navigate(path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const whyAiSubmenu = (
    <>
      <DropdownMenuItem onClick={() => handleNavigation('/case-study/faster-growth')}>
        <Brain className="h-4 w-4 text-electric-blue mr-2" />
        <span>Faster Growth</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => handleNavigation('/case-study/increased-efficiency')}>
        <Brain className="h-4 w-4 text-electric-blue mr-2" />
        <span>Increased Efficiency</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => handleNavigation('/case-study/time-saving')}>
        <Brain className="h-4 w-4 text-electric-blue mr-2" />
        <span>Time Savings</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => handleNavigation('/case-study/cost-optimization')}>
        <Brain className="h-4 w-4 text-electric-blue mr-2" />
        <span>Cost Optimization</span>
      </DropdownMenuItem>
    </>
  );

  const servicesSubmenu = (
    <>
      <DropdownMenuItem onClick={() => handleNavigation('/chatbot-development')}>
        <Wrench className="h-4 w-4 text-electric-blue mr-2" />
        <span>AI Chatbot Development</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => handleNavigation('/automated-outreach')}>
        <Wrench className="h-4 w-4 text-electric-blue mr-2" />
        <span>Automated Outreach Service</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => handleNavigation('/follow-up-system')}>
        <Wrench className="h-4 w-4 text-electric-blue mr-2" />
        <span>Automated Follow-up System</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => handleNavigation('/website-optimization')}>
        <Wrench className="h-4 w-4 text-electric-blue mr-2" />
        <span>Website Optimization</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => handleNavigation('/app-development')}>
        <Wrench className="h-4 w-4 text-electric-blue mr-2" />
        <span>App Development</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => handleNavigation('/custom-automation')}>
        <Wrench className="h-4 w-4 text-electric-blue mr-2" />
        <span>Custom Built Automation</span>
      </DropdownMenuItem>
    </>
  );

  const offersSubmenu = (
    <>
      <DropdownMenuItem onClick={() => handleNavigation('/hand-over-the-keys')}>
        <Tag className="h-4 w-4 text-electric-blue mr-2" />
        <span>Hand Over the Keys</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => handleNavigation('/keep-the-engine-running')}>
        <Tag className="h-4 w-4 text-electric-blue mr-2" />
        <span>Keep the Engine Running</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => handleNavigation('/drive-the-future')}>
        <Tag className="h-4 w-4 text-electric-blue mr-2" />
        <span>Drive the Future</span>
      </DropdownMenuItem>
    </>
  );

  const menuItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Info, label: 'About Us', path: '/about' },
    { icon: Brain, label: 'Why AI', path: '/#why-ai', submenu: whyAiSubmenu, submenuId: 'why-ai' },
    { icon: Wrench, label: 'Services', path: '/#services', submenu: servicesSubmenu, submenuId: 'services' },
    { icon: Star, label: 'Success Stories', path: '/#success-stories' },
    { icon: Tag, label: 'Offers', path: '/#offers', submenu: offersSubmenu, submenuId: 'offers' },
    { icon: HelpCircle, label: 'FAQs', path: '/faqs' },
    { icon: Mail, label: 'Contact Us', path: '/contact' },
    { icon: Calendar, label: 'Book a Consultation', path: '/book' },
    { icon: Scale, label: 'Terms of Service', path: '/terms-of-service' },
    { icon: Shield, label: 'Privacy Policy', path: '/privacy-policy' }
  ];

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-deep-blue/95 backdrop-blur-sm border-b border-accent-blue/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />
        <div className="flex items-center space-x-4">
          <nav className="hidden md:flex items-center space-x-2">
            {navItems.map((item) => {
              const isBookNow = item.tile === "BOOK NOW";
              const isActive = location.pathname + location.hash === item.path;
              
              return (
                <button
                  key={item.id}
                  className={cn(
                    "relative duration-300 transition-colors font-mono tracking-wide",
                    isBookNow 
                      ? "px-6 py-2 bg-electric-blue text-dark-blue rounded-lg hover:bg-electric-blue/90"
                      : cn(
                          "px-4 py-2 hover:text-electric-blue",
                          isActive ? "text-electric-blue" : "text-text-light/80"
                        )
                  )}
                  onClick={() => handleNavigation(item.path)}
                >
                  {item.tile}
                </button>
              );
            })}
          </nav>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  "group relative p-2 rounded-lg border transition-all duration-300 ease-out",
                  "hover:scale-110 hover:rotate-3 hover:-translate-y-0.5",
                  "active:scale-95 active:rotate-0 active:translate-y-0",
                  "bg-deep-blue border-accent-blue/20 hover:bg-accent-blue/10 hover:border-electric-blue/50"
                )}
                aria-label="Open menu"
              >
                {/* Background glow effect on hover */}
                <div className="absolute inset-0 rounded-lg bg-electric-blue/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
                
                {/* Menu icon with hover effects */}
                <Menu className={cn(
                  "relative z-10 h-5 w-5 transition-all duration-300",
                  "text-text-light group-hover:text-electric-blue",
                  "group-hover:scale-110 group-active:scale-90"
                )} />
                
                {/* Ripple effect */}
                <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute inset-0 rounded-lg border border-electric-blue/30 animate-pulse" />
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <React.Fragment key={item.label}>
                    <DropdownMenuItem 
                      onClick={item.submenu ? undefined : () => handleNavigation(item.path)}
                      submenu={item.submenu}
                      submenuId={item.submenuId}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-electric-blue flex-shrink-0" />
                        <span className="font-mono">{item.label}</span>
                      </div>
                    </DropdownMenuItem>
                    {index < menuItems.length - 1 && <DropdownMenuSeparator />}
                  </React.Fragment>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default Navbar;